import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { formatCurrency } from '../lib/utils';
import axios from 'axios';
import {
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  Truck,
  AlertTriangle,
  DollarSign,
  ShoppingCart,
  Wallet,
  CreditCard,
  Banknote,
  UserCog,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#c2185b', '#4caf50', '#ff9800', '#2196f3', '#9c27b0'];

export default function Dashboard() {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [expensesReport, setExpensesReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, salesRes, expensesRes] = await Promise.allSettled([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/reports/sales?period=month`),
          axios.get(`${API}/reports/expenses?period=month`),
        ]);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (salesRes.status === 'fulfilled') setSalesReport(salesRes.value.data);
        if (expensesRes.status === 'fulfilled') setExpensesReport(expensesRes.value.data);
      } catch (error) {
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const statCards = [
    {
      key: 'todaySales',
      value: stats?.today_sales || 0,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      key: 'monthSales',
      value: stats?.month_sales || 0,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      key: 'monthExpenses',
      value: stats?.month_expenses || 0,
      icon: Wallet,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      key: 'monthSalaries',
      label: language === 'ar' ? 'رواتب الشهر' : 'Month Salaries',
      value: stats?.month_salaries || 0,
      icon: Banknote,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      key: 'totalDebt',
      value: stats?.total_debt || 0,
      icon: CreditCard,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      key: 'netProfit',
      value: stats?.net_profit || 0,
      icon: stats?.net_profit >= 0 ? TrendingUp : TrendingDown,
      color: stats?.net_profit >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: stats?.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100',
    },
  ];

  const quickStats = [
    { key: 'products', value: stats?.products_count || 0, icon: Package },
    { key: 'customers', value: stats?.customers_count || 0, icon: Users },
    { key: 'suppliers', value: stats?.suppliers_count || 0, icon: Truck },
    { key: 'employees', value: stats?.employees_count || 0, icon: UserCog },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground" data-testid="dashboard-title">
            {t('dashboard')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'ar' ? 'مرحباً بك في نظام المحاسبة' : 'Welcome to your accounting system'}
          </p>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.key}
              className={`animate-slide-up stagger-${index + 1} hover:shadow-lg transition-all duration-300 border-border/40`}
              data-testid={`stat-card-${stat.key}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.label || t(stat.key)}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${stat.color}`}>
                      {stat.isCurrency === false ? stat.value : formatCurrency(stat.value)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className={stat.color} size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle className="font-heading text-xl">
              {language === 'ar' ? 'مبيعات الشهر' : 'Monthly Sales'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="sales-chart">
              {salesReport?.chart_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesReport.chart_data}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c2185b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#c2185b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [formatCurrency(value), t('total')]}
                      contentStyle={{ borderRadius: 8 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#c2185b"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorSales)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  {t('noData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Pie Chart */}
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle className="font-heading text-xl">
              {language === 'ar' ? 'المصروفات حسب الفئة' : 'Expenses by Category'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="expenses-chart">
              {expensesReport?.by_category?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensesReport.by_category}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="amount"
                      nameKey="category"
                      label={({ category, percent }) =>
                        `${category} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {expensesReport.by_category.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  {t('noData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Stats */}
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle className="font-heading text-xl">{t('quickStats')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {quickStats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.key}
                    className="text-center p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`quick-stat-${stat.key}`}
                  >
                    <Icon className="mx-auto text-primary mb-2" size={28} />
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{t(stat.key)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
